import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import type { PrototypeLogLine } from '@aiai/shared';
import { getToken } from '../lib/token';
import { refreshAccessToken } from '../lib/api';
import { getActiveOrgId } from '../lib/activeOrg';

export type SocketStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

export interface PrototypeLogHandlers {
  onLine?: (line: PrototypeLogLine) => void;
  onEnd?: () => void;
  onError?: (message: string) => void;
  onReconnect?: () => void;
}

const SOCKET_BASE = import.meta.env.VITE_API_URL ?? '';

/**
 * Subscribes to the `/prototype-logs` namespace for one prototype and routes
 * live container-log events to handlers. Connects only while `enabled` is true
 * (the opt-in Stream toggle), so a page view alone never opens a Docker stream.
 * Mirrors useRunSocket: bounded reconnect + silent token refresh on an expired
 * access token.
 */
export function usePrototypeLogSocket(
  prototypeId: string | undefined,
  enabled: boolean,
  handlers: PrototypeLogHandlers,
): SocketStatus {
  const [status, setStatus] = useState<SocketStatus>('connecting');
  const handlersRef = useRef<PrototypeLogHandlers>(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!prototypeId || !enabled) return;

    const socket: Socket = io(`${SOCKET_BASE}/prototype-logs`, {
      path: '/socket.io',
      transports: ['websocket'],
      auth: { token: getToken() },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 8000,
    });

    setStatus('connecting');
    let hasConnected = false;

    socket.on('connect', () => {
      const reconnected = hasConnected;
      hasConnected = true;
      setStatus('connected');
      socket.emit('subscribe', { prototypeId, orgId: getActiveOrgId() });
      if (reconnected) handlersRef.current.onReconnect?.();
    });
    socket.io.on('reconnect_attempt', () => setStatus('reconnecting'));
    socket.on('disconnect', (reason: string) => {
      setStatus('disconnected');
      // Gateway force-disconnects on an expired/invalid token ('io server
      // disconnect'), which socket.io-client never auto-retries. Refresh
      // silently and reconnect so an open panel keeps streaming.
      if (reason === 'io server disconnect') {
        setStatus('reconnecting');
        refreshAccessToken().then((newToken) => {
          if (newToken) {
            socket.auth = { token: newToken };
            socket.connect();
          } else {
            setStatus('disconnected');
          }
        });
      }
    });
    socket.on('connect_error', () =>
      setStatus(hasConnected ? 'reconnecting' : 'disconnected'),
    );

    socket.on('prototype-log', (line: PrototypeLogLine) => handlersRef.current.onLine?.(line));
    socket.on('prototype-log-end', () => handlersRef.current.onEnd?.());
    socket.on('prototype-log-error', (p: { message: string }) =>
      handlersRef.current.onError?.(p.message),
    );

    return () => {
      socket.emit('unsubscribe', { prototypeId });
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [prototypeId, enabled]);

  return status;
}
